import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { listAdminSessions, type SessionSummary } from '@/api/client'
import { AppShell } from '@/components/layout/AppShell'
import { DataTable, type DataTableColumn } from '@/components/spectrumui/data-table'
import { EmptyState } from '@/components/spectrumui/empty-state'
import { NumberTicker } from '@/components/motion/number-ticker'
import { recBadge, statusBadge, formatDate } from '@/lib/dashboard-utils'
import { usePageMeta } from '@/hooks/use-page-meta';

export function AdminDashboard() {
  usePageMeta("All interviews — Aura Admin")
  const navigate = useNavigate()
  const [sessions, setSessions] = useState<SessionSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSessions(null)
    setError(null)
    listAdminSessions()
      .then(data => { if (!cancelled) setSessions(data) })
      .catch(err => {
        if (cancelled) return
        console.error('Failed to load admin sessions:', {
          status: err?.response?.status,
          message: err?.message,
          url: err?.config?.url
        })
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          navigate('/', { replace: true })
          return
        }
        setError('Failed to load sessions. Please try again.')
      })
    return () => { cancelled = true }
  }, [navigate])

  const stats = useMemo(() => {
    const list = sessions ?? []
    const total = list.length
    const completed = list.filter(s => s.status === 'completed')
    const completionRate = total === 0 ? 0 : Math.round((completed.length / total) * 100)
    const scoredSessions = completed.filter(s => s.overall_score !== null)
    const avgScore = scoredSessions.length === 0
      ? null
      : Math.round(scoredSessions.reduce((sum, s) => sum + (s.overall_score ?? 0), 0) / scoredSessions.length)
    return { total, completed: completed.length, completionRate, avgScore }
  }, [sessions])

  // Sorting, search, the status pills and pagination all live in the
  // DataTable now. Newest-first stays the default: admins scan for the
  // latest runs.
  const columns: DataTableColumn<SessionSummary>[] = useMemo(() => [
    {
      id: 'candidate',
      header: 'Candidate',
      value: s => s.candidate_name,
    },
    {
      id: 'created',
      header: 'Created',
      sortable: true,
      // Epoch (not the formatted string) is what sorting compares.
      value: s => new Date(s.created_at).getTime(),
      cell: s => formatDate(s.created_at),
      hideBelow: 'sm',
    },
    {
      id: 'recommendation',
      header: 'Recommendation',
      value: s => s.recommendation ?? '',
      cell: s => {
        const rec = s.overall_score !== null && s.recommendation ? recBadge(s.recommendation) : null
        return rec ? <span className={rec.cls}>{rec.text}</span> : <span className="badge muted">—</span>
      },
      hideBelow: 'md',
    },
    {
      id: 'score',
      header: 'Score',
      numeric: true,
      sortable: true,
      value: s => s.overall_score ?? undefined,
      cell: s => s.overall_score ?? '—',
    },
    {
      id: 'status',
      header: 'Status',
      value: s => s.status,
      cell: s => {
        const status = statusBadge(s.status)
        return <span className={status.cls}>{status.text}</span>
      },
    },
  ], [])

  return (
    <AppShell active="admin">
      {error && <div className="wbanner" role="alert">{error}</div>}

      {sessions === null && !error ? (
        <div className="wloading" role="status" aria-live="polite">
          <span className="wspinner" aria-hidden="true"></span>
          <span>Loading sessions…</span>
        </div>
      ) : (
        <>
          <header className="screen-head">
            <div>
              <h1>All interviews</h1>
              <p>Every session across all candidates, with scores, recommendations, and direct links to full reports.</p>
            </div>
          </header>

          <section className="stats wadmin-stats" role="group" aria-label="Dashboard statistics">
            <article className="stat is-featured">
              <div className="stat-top">
                <span className="stat-label">Total sessions</span>
                <span className="stat-arrow" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M7 17L17 7M9 7h8v8"/></svg></span>
              </div>
              {/* Same tickers the dashboards use — admin was the only surface
                  rendering plain-text stats. */}
              <strong className="stat-value"><NumberTicker value={stats.total} startOnView={false} /></strong>
              <span className="stat-hint">All time</span>
            </article>
            <article className="stat">
              <div className="stat-top">
                <span className="stat-label">Completed</span>
                <span className="stat-arrow" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M7 17L17 7M9 7h8v8"/></svg></span>
              </div>
              <strong className="stat-value"><NumberTicker value={stats.completed} startOnView={false} /></strong>
              <span className="stat-hint">{stats.completionRate}% completion rate</span>
            </article>
            <article className="stat">
              <div className="stat-top">
                <span className="stat-label">Average score</span>
                <span className="stat-arrow" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M7 17L17 7M9 7h8v8"/></svg></span>
              </div>
              <strong className="stat-value">
                {stats.avgScore === null ? '—' : <NumberTicker value={stats.avgScore} startOnView={false} />}
              </strong>
              <span className="stat-hint">Across completed sessions</span>
            </article>
          </section>

          <DataTable
            data={sessions ?? []}
            columns={columns}
            rowId={s => s.session_id}
            rowLabel={s => `Session for ${s.candidate_name}`}
            caption="All interview sessions across candidates"
            variant="panel"
            searchable
            searchPlaceholder="Search by candidate name…"
            quickFilter={{
              columnId: 'status',
              label: 'Filter by status',
              options: [
                { value: 'pending', label: 'Pending' },
                { value: 'in_progress', label: 'In progress' },
                { value: 'completed', label: 'Completed' },
              ],
            }}
            defaultSort={{ columnId: 'created', direction: 'desc' }}
            pageSize={12}
            onRowClick={s => navigate(`/admin/session/${s.session_id}`)}
            emptyState={
              <EmptyState
                eyebrow="Nothing here yet"
                title="No sessions match"
                description="Once candidates run their first interview, every session shows up here — try clearing the search or status filter."
              />
            }
          />
        </>
      )}
    </AppShell>
  )
}
